import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import * as con from "../../GlobalConstants";
import { postUserActivity } from "../../GlobalFunctions";
import { getFinanceRequestConfig } from "../logic/supportFunctions";
import { extractErrorMessage } from "../../utils/errorFunctions";
import { createError } from "../../templates/notifications/Notifications";

// =====================================================
// THUNK: UPDATE SPOT
// =====================================================

export const updateSpot = createAsyncThunk(
  "market/updateSpot",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const currentSpot =
        state[con.REDUCER_MARKET_VALUES]?.[con.SPOT];

      const config = getFinanceRequestConfig();

      const res = await axios.get(
        con.rest_finance_url + con.spot_delay_url,
        config
      );

      if (res.data[con.STATUS] !== con.OK) {
        return rejectWithValue("SPOT_ERROR");
      }

      const newSpot = Number(res.data[con.SPOT].toFixed(2));

      await postUserActivity("refresh Spot");

      return {
        newSpot,
        previousSpot: currentSpot,
        lastUpdate: res.data[con.DATES].slice(-8),
      };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);


// =====================================================
// THUNK: FETCH ALL MARKET DATA
// =====================================================

export const fetchAllMarketData = createAsyncThunk(
  "market/fetchAllMarketData",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();
      const res = await axios.get(
        con.rest_finance_url + con.get_all_market_data_url,
        config
      );

      const data = res.data;

      // Historic dollar info
      if (data[con.HISTORIC_DOLLAR_INFO]?.[con.STATUS] === con.OK) {
      } else {
        dispatch(fetchDollarInfo());
      }

      // Market summaries
      if (data[con.MARKET_SUMMARIES]?.[con.STATUS] !== con.OK) {
        dispatch(fetchMarketSummaries());
      }

      // Forward curve
      if (data[con.MARKET_FORWARD_CURVE]?.[con.STATUS] !== con.OK) {
        dispatch(fetchForwardCurve());
      }

      // IBR
      if (data[con.MARKET_IBR]?.[con.STATUS] !== con.OK) {
        dispatch(fetchIBRCurve());
      }
      return data;

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando datos de mercado"));
    }
  }
);


export const fetchInitialTRM = createAsyncThunk(
  "market/fetchInitialTRM",
  async (_, { rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();

      const url = con.rest_finance_url + con.intraday_dollar_info;

      const res = await axios.get(url, config);

      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando la TRM"));
    }
  }
);

// =====================================================
// THUNK: FETCH DOLLAR INFO
// =====================================================

export const fetchDollarInfo = createAsyncThunk(
  "market/fetchDollarInfo",
  async (_, { rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();

      const url = con.rest_finance_url + con.historic_dollar_info_url;

      const res = await axios.get(url, config);

      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando histórico del dólar"));
    }
  }
);

// =====================================================
// THUNK: FETCH MARKET SUMMARIES
// =====================================================

export const fetchMarketSummaries = createAsyncThunk(
  "market/fetchMarketSummaries",
  async (_, { rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();

      const url = con.rest_finance_url + con.market_summaries_url;

      const res = await axios.get(url, config);
      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando resúmenes de mercado"));
    }
  }
);


// =====================================================
// THUNK: FETCH FORWARD CURVE
// =====================================================

export const fetchForwardCurve = createAsyncThunk(
  "market/fetchForwardCurve",
  async (_, { rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();

      const url = con.rest_finance_url + con.market_forward_curve_url;

      const res = await axios.get(url, config);

      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando la curva forward"));
    }
  }
);


// =====================================================
// THUNK: FETCH UBR CURVE
// =====================================================

export const fetchIBRCurve = createAsyncThunk(
  "market/fetchIBRCurve",
  async (_, { rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();

      const url = con.rest_finance_url + con.market_ibr_url;

      const res = await axios.get(url, config);

      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando la curva IBR"));
    }
  }
);



// =====================================================
// INITIAL STATE
// =====================================================

const initialState = {
  [con.SPOT]: 5000,
  [con.SPOT_SCHEME] : con.AUTOMATIC,
  [con.LAST_SPOT_UPDATE]: null,
  [con.SPOT_DIRECTION]: con.NEUTRAL,
  [con.SPOT_STATUS]: con.NOT_STARTED,

  [con.TRMR_REAL]: 5000,
  trmr_real_status: con.NOT_STARTED,

  [con.HISTORIC_DOLLAR_INFO]: {[con.DATES] : [], [con.TRM] : [], [con.CLOSE] : []},
  historic_dollar_info_status: con.NOT_STARTED,
  [con.MARKET_SUMMARIES] : { // The different market summaries (volatility and average per time window)
                                                  [con.MARKET_5_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800},
                                                  [con.MARKET_10_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800},
                                                  [con.MARKET_30_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800},
                                                  [con.MARKET_90_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800},
                                                  [con.MARKET_180_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800},
                                                  [con.MARKET_360_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800},
                                                  [con.MARKET_TOTAL_DAYS] : { [con.VOLATILITY] : 0.1, [con.AVERAGE] : 4800}
                                                   },
  market_summaries_status: con.NOT_STARTED,
  [con.MARKET_FORWARD_CURVE] : {}, // Current (latest) market curve
  forward_curve_status: con.NOT_STARTED,
  [con.MARKET_IBR] : {}, // Current (latest) market curve
  ibr_curve_status: con.NOT_STARTED,
  [con.MARKET_VOLATILITY]: 0.15,


};

// =====================================================
// SLICE
// =====================================================

const marketValuesSlice = createSlice({
  name: con.REDUCER_MARKET_VALUES,
  initialState,
  reducers: {
    setMarketValue: (state, action) => {
      const { id, value } = action.payload;
      state[id] = value;
    },
  },

  extraReducers: (builder) => {
    builder

      // ================= UPDATE SPOT =================

      .addCase(updateSpot.pending, (state) => {
        state[con.SPOT_STATUS] = con.LOADING;
      })

      .addCase(updateSpot.fulfilled, (state, action) => {
        const { newSpot, previousSpot, lastUpdate } = action.payload;

        state[con.SPOT_STATUS] = con.OK;

        if (previousSpot !== null && previousSpot !== newSpot) {
          state[con.SPOT_DIRECTION] =
            previousSpot < newSpot ? con.UP : con.DOWN;

          console.log("Spot Changed");
        }

        state[con.SPOT] = newSpot;
        state[con.LAST_SPOT_UPDATE] = lastUpdate;

        console.log("Spot Updated");
      })

      .addCase(updateSpot.rejected, (state) => {
        state[con.SPOT_STATUS] = con.ERROR;
      })

      // ================= ALL MARKET DATA =================
      .addCase(fetchAllMarketData.pending, (state) => {
        state.historic_dollar_info_status = con.LOADING;
        state.market_summaries_status = con.LOADING;
        state.forward_curve_status = con.LOADING;
        state.ibr_curve_status = con.LOADING;
      })

      .addCase(fetchAllMarketData.fulfilled, (state, action) => {
        const data = action.payload;
        // Historic dollar info
        const dollarData = data[con.HISTORIC_DOLLAR_INFO];
        if (dollarData?.[con.STATUS] === con.OK) {
          state[con.HISTORIC_DOLLAR_INFO] = dollarData[con.HISTORIC_DOLLAR_INFO];
          state.historic_dollar_info_status = con.OK;
        } else {
          state.historic_dollar_info_status = con.ERROR;
        }
        // Market summaries
        const summaries = data[con.MARKET_SUMMARIES];
        if (summaries?.[con.STATUS] === con.OK) {
          state[con.MARKET_SUMMARIES] = summaries[con.MARKET_SUMMARIES];
          state[con.MARKET_VOLATILITY] = summaries[con.MARKET_SUMMARIES][con.DEFAULT_MARKET_PERIOD][con.VOLATILITY];
          state.market_summaries_status = con.OK;
        } else {
          state.market_summaries_status = con.ERROR;
        }
        // Forward curve
        const forwardCurve = data[con.MARKET_FORWARD_CURVE];
        if (forwardCurve?.[con.STATUS] === con.OK) {
          state[con.MARKET_FORWARD_CURVE] = forwardCurve[con.MARKET_FORWARD_CURVE];
          state.forward_curve_status = con.OK;
        } else {
          state.forward_curve_status = con.ERROR;
        }
        // IBR
        const ibr = data[con.MARKET_IBR];
        if (ibr?.[con.STATUS] === con.OK) {
          state[con.MARKET_IBR] = ibr[con.MARKET_IBR];
          state.ibr_curve_status = con.OK;
        } else {
          state.ibr_curve_status = con.ERROR;
        }
      })
      .addCase(fetchAllMarketData.rejected, (state, action) => {
        if (state.historic_dollar_info_status === con.LOADING)
          state.historic_dollar_info_status = con.ERROR;

        if (state.market_summaries_status === con.LOADING)
          state.market_summaries_status = con.ERROR;

        if (state.forward_curve_status === con.LOADING)
          state.forward_curve_status = con.ERROR;

        if (state.ibr_curve_status === con.LOADING)
          state.ibr_curve_status = con.ERROR;
        createError(action.payload);
      })

      // ================= FETCH TRM =================
      .addCase(fetchInitialTRM.pending, (state) => {
        state.trmr_real_status = con.LOADING;
      })
      .addCase(fetchInitialTRM.fulfilled, (state, action) => {
        state.trmr_real_status = con.OK;
        state.trmr_real = action.payload;
      })
      .addCase(fetchInitialTRM.rejected, (state, action) => {
        state.trmr_real_status = con.ERROR;
        createError(action.payload);
      })

      // ================= FETCH DOLLAR =================
      .addCase(fetchDollarInfo.pending, (state) => {
        state.historic_dollar_info_status = con.LOADING;
      })
      .addCase(fetchDollarInfo.fulfilled, (state, action) => {
        state.historic_dollar_info_status = con.OK;
        state.historic_dollar_info = action.payload[con.HISTORIC_DOLLAR_INFO];
      })
      .addCase(fetchDollarInfo.rejected, (state, action) => {
        state.historic_dollar_info_status = con.ERROR;
        createError(action.payload);
      })

      // ================= FETCH MARKET SUMMARIES =================
      .addCase(fetchMarketSummaries.pending, (state) => {
        state.market_summaries_status = con.LOADING;
      })
      .addCase(fetchMarketSummaries.fulfilled, (state, action) => {
        state.market_summaries_status = con.OK;
        state.market_summaries = action.payload[con.MARKET_SUMMARIES];
        state.market_volatility = action.payload[con.MARKET_SUMMARIES][con.DEFAULT_MARKET_PERIOD][con.VOLATILITY];
      })
      .addCase(fetchMarketSummaries.rejected, (state, action) => {
        state.market_summaries_status = con.ERROR;
        createError(action.payload);
      })

      // ================= FETCH FORWARD CURVE =================
      .addCase(fetchForwardCurve.pending, (state) => {
        state.forward_curve_status = con.LOADING;
      })
      .addCase(fetchForwardCurve.fulfilled, (state, action) => {
        state.forward_curve_status = con.OK;
        state.forward_curve = action.payload;
      })
      .addCase(fetchForwardCurve.rejected, (state, action) => {
        state.forward_curve_status = con.ERROR;
        createError(action.payload);
      })

      // ================= FETCH IBR CURVE =================
      .addCase(fetchIBRCurve.pending, (state) => {
        state.ibr_curve_status = con.LOADING;
      })
      .addCase(fetchIBRCurve.fulfilled, (state, action) => {
      state.ibr_curve_status = con.OK;
        state.ibr = action.payload;
      })
      .addCase(fetchIBRCurve.rejected, (state, action) => {
        state.ibr_curve_status = con.ERROR;
        createError(action.payload);
      })

  },
});

// =====================================================
// EXPORT
// =====================================================

export const {setMarketValue} = marketValuesSlice.actions;


export default marketValuesSlice.reducer;
